import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Row, Col, Card, Slider, Button, Icon} from 'antd';
import _ from 'lodash';

import * as mapActions from '../actions/map-actions';
import LineChart from '../charts/LineChart';
import HorizBarChart from '../charts/HorizBarChart';
import PercentRadial from '../charts/PercentRadial';

import ReadMore from '../components/ReadMore.js';
import CardMenu from './CardMenu.js';

import './DemographicsCard.css';

class DemographicsCard extends Component {

  constructor(props) {
    super(props);

    this.state = {
      playing: false,
      sliderYear: 2015,
      year: 2015
    }
  }

  onAfterChange(value) {
    this.stop();
    this.setState({year: value, sliderYear: value});
  }

  componentWillUnmount() {
    this.stop();
  }

  backClick() {
    this.stop();

    let newYear = this.state.year - 1;
    if (newYear < 2000) {
      newYear = 2015;
    }
    this.setState({year: newYear, sliderYear: newYear});
  }

  forwardClick() {
    this.stop();

    let newYear = this.state.year + 1;
    if (newYear > 2015) {
      newYear = 2000;
    }
    this.setState({year: newYear, sliderYear: newYear});
  }

  playClick() {
    this.state.playing ? this.stop() : this.play();
  }

  play() {
    this.setState({year: 2000, sliderYear: 2000, playing: true});
    this.timer = setInterval(this.tick.bind(this), 700);
  }

  stop() {
    this.setState({playing: false});
    clearInterval(this.timer);
  }

  tick() {
    let newYear = this.state.year + 1;

    if (newYear > 2015) {
      this.stop();
    }
    else {
      this.setState({year: newYear, sliderYear: newYear});
    }
  }

  render() {
    const marks = {
      2000: '2000',
      2001: '2001',
      2002: '2002',
      2003: '2003',
      2004: '2004',
      2005: '2005',
      2006: '2006',
      2007: '2007',
      2008: '2008',
      2009: '2009',
      2010: '2010',
      2011: '2011',
      2012: '2012',
      2013: '2013',
      2014: '2014',
      2015: '2015'
    };

    const playBtn = (
      <Button.Group type="primary" size="small" style={{marginTop: '-4px'}}>
        <Button onClick={this.backClick.bind(this)}>
          <Icon type="step-backward"/>
        </Button>
        <Button onClick={this.forwardClick.bind(this)}>
          <Icon type="step-forward"/>
        </Button>
        <Button onClick={this.playClick.bind(this)}>
          <Icon type={this.state.playing ? 'pause' : 'caret-right'} style={{color: '#c85a5a'}}/>
        </Button>
      </Button.Group>
    );

    const title = (
      <CardMenu showDemographics={true} title={
        <span><span className="hidden-sm-down">National </span>Overdose Demographics</span>
      }/>
    );

    return (
      <Card className="DemographicsCard" title={title} extra={playBtn} noHovering={true} loading={this.props.loading}>
        <Row gutter={35}>
          <Col xs={24} md={6} xl={5}>
            <p>Although drug related deaths have increased dramatically nationwide,
              there are no clear trending demographic identifiers associated with the rise.</p>

            <p className="lead">
              These visualizations allows you to view and filter drug overdose deaths
              by year, age, sex, and race demographics.
            </p>

            <ReadMore>
              <p>The overdose age ranges are displayed in the bar chart on the left, with higher values rendering
                darker. The gender breakdown is displayed in the doughnut graphs to the right of the age bar charts,
                and the overdose rate for each race demographic over time is displayed below.</p>

              <p>You can filter the corresponding information using the menu dropdown <i>(upper left)</i>, or by
                clicking on the charts themselves. For example, by clicking on a color of the sex chart, you can update
                the age and race charts, or by clicking on an age range bar the race and sex charts will filter
                accordingly.</p>

              <p><b>Source Data:</b><br/>
                <a href="https://www.cdc.gov/nchs/data-visualization/drug-poisoning-mortality/" target="_blank" rel="noopener noreferrer">
                  CDC & NCHS - Drug Poisoning Mortality Data
                </a>
              </p>
            </ReadMore>
          </Col>
          <Col xs={24} md={18} xl={19}>
            <Row gutter={15} type="flex">
              <Col xs={24} md={10}>
                <h2>US Overdose Deaths by Age {this.state.year}</h2>
                <h3>{this.props.sex} : {this.props.race}</h3>
                <HorizBarChart
                  dimX="deaths"
                  dimY="age"
                  data={this.props.ageData[this.state.year]}
                  onClick={function (d) {
                    this.props.actions.selectAge(d.age);
                  }.bind(this)}
                />
              </Col>
              <Col xs={24} md={14}>
                <Row gutter={0} className="chart-wrapper">
                  <h2>US Overdose Deaths by Gender {this.state.year}</h2>
                  <h3>{this.props.race} : {this.props.age !== 'All Ages' ? ' Age' : ''} {this.props.age}</h3>
                  <Col xs={12}>
                    <PercentRadial
                      title="Male"
                      value={_.get(this.props.sexData, [this.state.year, 'Male', 'deaths'], 0)}
                      total={_.get(this.props.sexData, [this.state.year, 'Both Sexes', 'deaths'], 1)}
                      onClick={this.props.actions.selectSex}
                    />
                  </Col>
                  <Col xs={12}>
                    <PercentRadial
                      title="Female"
                      alternate={true}
                      value={_.get(this.props.sexData, [this.state.year, 'Female', 'deaths'], 0)}
                      total={_.get(this.props.sexData, [this.state.year, 'Both Sexes', 'deaths'], 1)}
                      onClick={this.props.actions.selectSex}
                    />
                  </Col>
                </Row>
                <div className="slider-wrapper">
                  <Slider vertical
                          dots={true}
                          included={false}
                          marks={marks}
                          value={this.state.sliderYear}
                          step={1}
                          min={2000}
                          max={2015}
                          onChange={d => this.setState({sliderYear: d})}
                          onAfterChange={this.onAfterChange.bind(this)}/>
                </div>
              </Col>
            </Row>
            <Row>
              <h2>
                US Overdose Deaths by Race per 100k
                <span className="hidden-sm-down">
                  &nbsp;: {this.props.sex} : {this.props.age !== 'All Ages' ? ' Age' : ''} {this.props.age}
                </span>
              </h2>
              <h3 className="hidden-md-up">
                {this.props.sex} : {this.props.age !== 'All Ages' ? ' Age' : ''} {this.props.age}
              </h3>
              <LineChart
                dimG="race"
                dimX="year"
                dimY="rate"
                titleX="Year"
                titleY="Overdose per 100k"
                highlightX={+this.state.year}
                data={this.props.raceData}
                onClick={function (d) {
                  this.props.actions.selectRace(d.race);
                }.bind(this)}
              />
            </Row>
          </Col>
        </Row>
      </Card>
    );
  }
}

function mapStoreToProps(store, props) {
  return {
    loading: store.demographics.loading,
    age: store.demographics.age,
    sex: store.demographics.sex,
    race: store.demographics.race,
    sexData: store.demographics.dataBySex,
    ageData: store.demographics.dataByAge,
    raceData: store.demographics.dataByRace
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(mapActions, dispatch)
  }
}

export default connect(mapStoreToProps, mapDispatchToProps)(DemographicsCard);



// WEBPACK FOOTER //
// ./src/containers/DemographicsCard.js